import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { supabase } from './supabase';
import UserDashboard from './UserDashboard';
import ComingSoonPage from './ComingSoonPage';

// ============================================================
// LASKY SL/TP SYNC — User-Facing App Router with Maintenance Mode
// © 2026 Shadow Technologies. All Rights Reserved.
//
// S11 (25 Apr 2026): Admin completely isolated to SuperAdmin.shadowtechlab.com
// - AdminDashboard import REMOVED
// - /admin route + bypass mechanism REMOVED
// - /admin* served as Vercel-level hard 404 (vercel.json) — React never loads
// - This bundle is USER-FACING ONLY
//
// MAINTENANCE LOGIC (FAIL-SAFE / FAIL-CLOSED):
// - Backend env MAINTENANCE_MODE=true → public routes show Coming Soon
// - If backend unreachable → DEFAULT TO MAINTENANCE (fail-closed for safety)
// ============================================================

const API_URL = "https://laskyslsync-cloud-production.up.railway.app";

// Check maintenance status from backend
// FAIL-CLOSED: if fetch fails, assume maintenance is ON (safer default)
function useMaintenanceStatus() {
  const [status, setStatus] = useState({ loading: true, maintenance: true });

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

    fetch(`${API_URL}/api/maintenance/status`, { signal: controller.signal })
      .then(r => {
        if (!r.ok) throw new Error('Non-OK response');
        return r.json();
      })
      .then(d => {
        if (mounted) {
          setStatus({
            loading: false,
            // Fail-closed: only set to false if backend explicitly says false
            maintenance: d.maintenance !== false,
          });
        }
      })
      .catch(() => {
        // Fetch failed → assume maintenance ON (fail-safe)
        if (mounted) setStatus({ loading: false, maintenance: true });
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      mounted = false;
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, []);

  return status;
}

// User route wrapper — checks maintenance mode
function UserRoute({ maintenance }) {
  if (maintenance) return <ComingSoonPage />;
  return <UserDashboard />;
}

// Loading state while we check maintenance status
function LoadingScreen() {
  return (
    <div style={{
      minHeight: "100vh",
      background: "#06060B",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#64748B",
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: 13,
      letterSpacing: "0.1em",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          border: "2px solid #1E293B",
          borderTopColor: "#22D3EE",
          animation: "spin 0.8s linear infinite",
        }} />
        <span>LOADING</span>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ============================================================
// SHARED AUTH CARD LAYOUT
// Common wrapper for all /auth/* success/form pages.
// ============================================================
function AuthCard({ children }) {
  return (
    <div style={{
      minHeight: "100vh",
      background: "#06060B",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      padding: 20,
    }}>
      <div style={{
        maxWidth: 440,
        width: "100%",
        background: "#0A0A12",
        border: "1px solid #1E293B",
        borderRadius: 16,
        padding: 40,
        textAlign: "center",
      }}>
        {children}
      </div>
    </div>
  );
}

function StatusIcon({ variant = "success" }) {
  const colors = {
    success: { bg: "rgba(34, 211, 238, 0.1)", border: "rgba(34, 211, 238, 0.3)", fg: "#22D3EE", symbol: "✓" },
    error:   { bg: "rgba(239, 68, 68, 0.1)",  border: "rgba(239, 68, 68, 0.3)",  fg: "#EF4444", symbol: "✕" },
    loading: { bg: "rgba(148, 163, 184, 0.1)", border: "rgba(148, 163, 184, 0.3)", fg: "#94A3B8", symbol: "" },
  };
  const c = colors[variant];
  return (
    <div style={{
      width: 64, height: 64, borderRadius: "50%",
      background: c.bg, border: `1px solid ${c.border}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      margin: "0 auto 24px", fontSize: 32, color: c.fg,
    }}>
      {variant === "loading" ? (
        <div style={{
          width: 28, height: 28, borderRadius: "50%",
          border: "2px solid #1E293B", borderTopColor: "#94A3B8",
          animation: "spin 0.8s linear infinite",
        }} />
      ) : c.symbol}
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled, style = {} }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "14px",
        background: disabled ? "#1E293B" : "#22D3EE",
        border: "none",
        borderRadius: 10,
        color: disabled ? "#64748B" : "#06060B",
        fontSize: 14,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "inherit",
        ...style,
      }}
    >{children}</button>
  );
}

// Read token_hash + type from URL query string (Supabase email links pass these)
function useAuthParams() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return {
    tokenHash: params.get("token_hash"),
    type: params.get("type"),
    error: params.get("error"),
    errorDescription: params.get("error_description"),
  };
}

// Auto-redirect hook with visible countdown
function useRedirectCountdown(seconds, target = "/", enabled = true) {
  const [count, setCount] = useState(seconds);
  useEffect(() => {
    if (!enabled) return;
    if (count <= 0) { window.location.href = target; return; }
    const t = setTimeout(() => setCount(count - 1), 1000);
    return () => clearTimeout(t);
  }, [count, enabled, target]);
  return count;
}

// Password strength calculator (mirrors UserDashboard signup logic)
function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: "", color: "#475569" };
  let x = 0;
  if (pw.length >= 8) x += 25;
  if (pw.length >= 12) x += 15;
  if (/[A-Z]/.test(pw)) x += 15;
  if (/[a-z]/.test(pw)) x += 10;
  if (/[0-9]/.test(pw)) x += 15;
  if (/[^A-Za-z0-9]/.test(pw)) x += 20;
  const score = Math.min(100, x);
  if (score >= 71) return { score, label: "Strong", color: "#22C55E" };
  if (score >= 41) return { score, label: "Medium", color: "#F59E0B" };
  return { score, label: "Weak", color: "#EF4444" };
}

// ============================================================
// /auth/verified — email confirmation success (signup)
// Flow: verify token_hash (type=signup) → show success → redirect /
// ============================================================
function VerifiedPage() {
  const { tokenHash, type, error: urlError } = useAuthParams();
  const [state, setState] = useState("loading"); // loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const countdown = useRedirectCountdown(5, "/", state === "success");

  useEffect(() => {
    if (urlError) {
      setState("error");
      setErrorMsg(urlError === "access_denied" ? "This verification link has expired or already been used." : urlError);
      return;
    }
    if (!tokenHash) {
      setState("error");
      setErrorMsg("Missing verification token. Please use the link from your email.");
      return;
    }
    (async () => {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type || "signup",
      });
      // Sign out immediately — Supabase auto-creates a session on verify,
      // but we want user to explicitly login on the main page.
      await supabase.auth.signOut();
      if (error) {
        setState("error");
        setErrorMsg(error.message || "Verification failed. The link may be expired.");
      } else {
        setState("success");
      }
    })();
  }, [tokenHash, type, urlError]);

  if (state === "loading") return (
    <AuthCard>
      <StatusIcon variant="loading" />
      <h1 style={{ color: "#E2E8F0", fontSize: 20, fontWeight: 600, margin: "0 0 8px" }}>Verifying your email...</h1>
      <p style={{ color: "#94A3B8", fontSize: 13, margin: 0 }}>Please wait a moment.</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </AuthCard>
  );

  if (state === "error") return (
    <AuthCard>
      <StatusIcon variant="error" />
      <h1 style={{ color: "#E2E8F0", fontSize: 24, fontWeight: 600, margin: "0 0 12px" }}>Verification Failed</h1>
      <p style={{ color: "#94A3B8", fontSize: 14, lineHeight: 1.6, margin: "0 0 28px" }}>{errorMsg}</p>
      <PrimaryButton onClick={() => { window.location.href = "/"; }}>Back to Login</PrimaryButton>
    </AuthCard>
  );

  return (
    <AuthCard>
      <StatusIcon variant="success" />
      <h1 style={{ color: "#E2E8F0", fontSize: 24, fontWeight: 600, margin: "0 0 12px" }}>Email Verified</h1>
      <p style={{ color: "#94A3B8", fontSize: 14, lineHeight: 1.6, margin: "0 0 28px" }}>
        Your email address has been successfully verified. You can now login to your account.
      </p>
      <PrimaryButton onClick={() => { window.location.href = "/"; }} style={{ marginBottom: 12 }}>Go to Login</PrimaryButton>
      <p style={{ color: "#475569", fontSize: 12, margin: 0 }}>Redirecting automatically in {countdown}s...</p>
    </AuthCard>
  );
}

// ============================================================
// /auth/email-changed — new email verification success
// Flow: verify token_hash (type=email_change) → success card → force re-login
// ============================================================
function EmailChangedPage() {
  const { tokenHash, type, error: urlError } = useAuthParams();
  const [state, setState] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const countdown = useRedirectCountdown(5, "/", state === "success");

  useEffect(() => {
    if (urlError) {
      setState("error");
      setErrorMsg(urlError === "access_denied" ? "This link has expired or already been used." : urlError);
      return;
    }
    if (!tokenHash) {
      setState("error");
      setErrorMsg("Missing verification token. Please use the link from your email.");
      return;
    }
    (async () => {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type || "email_change",
      });
      // Force re-login with new email
      await supabase.auth.signOut();
      if (error) {
        setState("error");
        setErrorMsg(error.message || "Email change failed. The link may be expired.");
      } else {
        setState("success");
      }
    })();
  }, [tokenHash, type, urlError]);

  if (state === "loading") return (
    <AuthCard>
      <StatusIcon variant="loading" />
      <h1 style={{ color: "#E2E8F0", fontSize: 20, fontWeight: 600, margin: "0 0 8px" }}>Updating your email...</h1>
      <p style={{ color: "#94A3B8", fontSize: 13, margin: 0 }}>Please wait a moment.</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </AuthCard>
  );

  if (state === "error") return (
    <AuthCard>
      <StatusIcon variant="error" />
      <h1 style={{ color: "#E2E8F0", fontSize: 24, fontWeight: 600, margin: "0 0 12px" }}>Email Change Failed</h1>
      <p style={{ color: "#94A3B8", fontSize: 14, lineHeight: 1.6, margin: "0 0 28px" }}>{errorMsg}</p>
      <PrimaryButton onClick={() => { window.location.href = "/"; }}>Back to Login</PrimaryButton>
    </AuthCard>
  );

  return (
    <AuthCard>
      <StatusIcon variant="success" />
      <h1 style={{ color: "#E2E8F0", fontSize: 24, fontWeight: 600, margin: "0 0 12px" }}>Email Changed</h1>
      <p style={{ color: "#94A3B8", fontSize: 14, lineHeight: 1.6, margin: "0 0 20px" }}>
        Your email address has been successfully updated.
      </p>
      <div style={{
        background: "rgba(34, 211, 238, 0.06)",
        border: "1px solid rgba(34, 211, 238, 0.2)",
        borderRadius: 10,
        padding: "14px 16px",
        marginBottom: 28,
        textAlign: "left",
      }}>
        <p style={{ color: "#22D3EE", fontSize: 12, fontWeight: 600, margin: "0 0 4px", letterSpacing: "0.05em" }}>IMPORTANT</p>
        <p style={{ color: "#CBD5E1", fontSize: 13, lineHeight: 1.5, margin: 0 }}>
          Please use your <strong>new email address</strong> to login from now on. Your old email will no longer work.
        </p>
      </div>
      <PrimaryButton onClick={() => { window.location.href = "/"; }} style={{ marginBottom: 12 }}>Go to Login</PrimaryButton>
      <p style={{ color: "#475569", fontSize: 12, margin: 0 }}>Redirecting automatically in {countdown}s...</p>
    </AuthCard>
  );
}

// ============================================================
// /auth/reset-password — password reset form
// Flow: verify recovery token → show form → updateUser → success → redirect
// ============================================================
function ResetPasswordPage() {
  const { tokenHash, type, error: urlError } = useAuthParams();
  const [state, setState] = useState("verifying"); // verifying | form | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const strength = getPasswordStrength(pw1);
  const countdown = useRedirectCountdown(5, "/", state === "success");

  // Verify recovery token on mount
  useEffect(() => {
    if (urlError) {
      setState("error");
      setErrorMsg(urlError === "access_denied" ? "This password reset link has expired or already been used." : urlError);
      return;
    }
    if (!tokenHash) {
      setState("error");
      setErrorMsg("Missing reset token. Please use the link from your email.");
      return;
    }
    (async () => {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type || "recovery",
      });
      if (error) {
        setState("error");
        setErrorMsg(error.message || "Invalid or expired reset link.");
      } else {
        // Token valid — session is now active, user can call updateUser
        setState("form");
      }
    })();
  }, [tokenHash, type, urlError]);

  const canSubmit = pw1.length >= 8 && pw1 === pw2 && strength.score >= 41;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setState("submitting");
    const { error } = await supabase.auth.updateUser({ password: pw1 });
    // Sign out so user must login fresh with new password
    await supabase.auth.signOut();
    if (error) {
      setState("error");
      setErrorMsg(error.message || "Failed to update password.");
    } else {
      setState("success");
    }
  };

  if (state === "verifying" || state === "submitting") return (
    <AuthCard>
      <StatusIcon variant="loading" />
      <h1 style={{ color: "#E2E8F0", fontSize: 20, fontWeight: 600, margin: "0 0 8px" }}>
        {state === "verifying" ? "Verifying reset link..." : "Updating password..."}
      </h1>
      <p style={{ color: "#94A3B8", fontSize: 13, margin: 0 }}>Please wait a moment.</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </AuthCard>
  );

  if (state === "error") return (
    <AuthCard>
      <StatusIcon variant="error" />
      <h1 style={{ color: "#E2E8F0", fontSize: 24, fontWeight: 600, margin: "0 0 12px" }}>Reset Failed</h1>
      <p style={{ color: "#94A3B8", fontSize: 14, lineHeight: 1.6, margin: "0 0 28px" }}>{errorMsg}</p>
      <PrimaryButton onClick={() => { window.location.href = "/"; }}>Back to Login</PrimaryButton>
    </AuthCard>
  );

  if (state === "success") return (
    <AuthCard>
      <StatusIcon variant="success" />
      <h1 style={{ color: "#E2E8F0", fontSize: 24, fontWeight: 600, margin: "0 0 12px" }}>Password Updated</h1>
      <p style={{ color: "#94A3B8", fontSize: 14, lineHeight: 1.6, margin: "0 0 28px" }}>
        Your password has been successfully changed. Please login with your new password.
      </p>
      <PrimaryButton onClick={() => { window.location.href = "/"; }} style={{ marginBottom: 12 }}>Go to Login</PrimaryButton>
      <p style={{ color: "#475569", fontSize: 12, margin: 0 }}>Redirecting automatically in {countdown}s...</p>
    </AuthCard>
  );

  // form state
  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    background: "#06060B",
    border: "1px solid #1E293B",
    borderRadius: 10,
    color: "#E2E8F0",
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
  };
  const labelStyle = { color: "#94A3B8", fontSize: 12, fontWeight: 500, marginBottom: 6, display: "block", textAlign: "left" };

  return (
    <AuthCard>
      <h1 style={{ color: "#E2E8F0", fontSize: 24, fontWeight: 600, margin: "0 0 8px" }}>Reset Your Password</h1>
      <p style={{ color: "#94A3B8", fontSize: 13, margin: "0 0 28px" }}>Enter a new password for your account.</p>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>New Password</label>
        <input
          type={showPw ? "text" : "password"}
          value={pw1}
          onChange={(e) => setPw1(e.target.value)}
          placeholder="Enter new password"
          style={inputStyle}
          autoFocus
        />
        {pw1 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ height: 3, borderRadius: 2, background: "#1E293B", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${strength.score}%`, background: strength.color, transition: "width 0.3s ease" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              <span style={{ color: strength.color, fontSize: 11, fontWeight: 600 }}>{strength.label}</span>
              <span style={{ color: "#475569", fontSize: 10 }}>min 8 chars recommended</span>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Confirm Password</label>
        <input
          type={showPw ? "text" : "password"}
          value={pw2}
          onChange={(e) => setPw2(e.target.value)}
          placeholder="Re-enter new password"
          onKeyDown={(e) => e.key === "Enter" && canSubmit && handleSubmit()}
          style={{
            ...inputStyle,
            borderColor: pw2 && pw1 !== pw2 ? "#EF4444" : "#1E293B",
          }}
        />
        {pw2 && pw1 !== pw2 && (
          <p style={{ color: "#EF4444", fontSize: 11, margin: "6px 0 0", textAlign: "left" }}>Passwords do not match</p>
        )}
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, cursor: "pointer", color: "#94A3B8", fontSize: 12 }}>
        <input type="checkbox" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} style={{ cursor: "pointer" }} />
        Show password
      </label>

      <PrimaryButton onClick={handleSubmit} disabled={!canSubmit}>Update Password</PrimaryButton>
    </AuthCard>
  );
}

function App() {
  const { loading, maintenance } = useMaintenanceStatus();

  if (loading) return <LoadingScreen />;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/auth/verified" element={<VerifiedPage />} />
        <Route path="/auth/email-changed" element={<EmailChangedPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/*" element={<UserRoute maintenance={maintenance} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
